import React, { useEffect, useContext, useState, useCallback } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import Routes from './Routes'
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, AuthContext } from './hooks/auth'

// styles
import GlobalStyle from './styles/global'
import HelpersStyle from './styles/helpers'
import { ModalOverlay, ModalContainer, ButtonActive, ButtonNormal } from '~/pages/Login/style'

import accessibilityIcon from '~/assets/icons/accessibility-icon.svg';
import eyeAccessibilityIcon from '~/assets/icons/eye-solid-full.svg';
import readAccessibilityIcon from '~/assets/icons/read-icon.svg';

import { getCookie, createCookie } from '~/services/functions'

import accessibility from 'accessibility';

function AppContent() {

  const auth = useContext(AuthContext);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [currentPref, setCurrentPref] = useState(localStorage.getItem('user_accessibility') || '99');
  const [isHovered, setIsHovered] = useState(false);

  const accessibilityOptions = {
    modules: {
      increaseTextSpacing: false,
      decreaseTextSpacing: false,
      increaseLineHeight: false,
      decreaseLineHeight: false
    },

    textPixelMode: true,
    textSizeFactor: 4,
    textToSpeechLang: 'pt-BR',
    speechToTextLang: 'pt-BR',
    labels: {
      resetTitle: 'Resetar configurações',
      closeTitle: 'Fechar menu',
      menuTitle: 'Menu de Acessibilidade',
      increaseText: 'Aumentar tamanho do texto',
      decreaseText: 'Diminuir tamanho do texto',
      increaseTextSpacing: 'Aumentar espaçamento do texto',
      decreaseTextSpacing: 'Diminuir espaçamento do texto',
      increaseLineHeight: 'Aumentar altura da linha',
      decreaseLineHeight: 'Diminuir altura da linha',
      invertColors: 'Inverter cores',
      grayHues: 'Tons de cinza',
      underlineLinks: 'Sublinhar links',
      bigCursor: 'Cursor grande',
      readingGuide: 'Guia de leitura',
      textToSpeech: 'Leitura de voz (Texto para Fala)',
      speechToText: 'Comando de voz (Fala para Texto)',
      disableAnimations: 'Desativar animações',
      hotkeyPrefix: 'Atalho:',
    }
  };

  const initAccessibility = useCallback((value) => {
    const recurso = String(value);
    localStorage.setItem('user_accessibility', recurso);

    if (recurso === "1") {
      if (!document.getElementById('plugin-blind-script')) {
        const script = document.createElement('script');
        script.id = 'plugin-blind-script';
        script.src = "/plugin_blind_accessibility.js";
        script.async = true;
        document.head.appendChild(script);
      }
    } else if (recurso === "2") {
      if (window.widgetJaIniciado) return;
      window.widgetJaIniciado = true;

      const optionsComPersistencia = {
        ...accessibilityOptions,
        session: {
          persistent: true
        }
      };

      if (window.Accessibility) {
        new window.Accessibility(optionsComPersistencia);
      } else {
        const script = document.createElement('script');
        script.src = "https://cdn.jsdelivr.net/npm/accessibility@1.2.0/dist/main.bundle.js";
        script.async = true;
        script.onload = () => {
          new window.Accessibility(optionsComPersistencia);
        };
        document.head.appendChild(script);
      }
    }
  }, []);

  const getIcon = () => {
    const pref = localStorage.getItem('user_accessibility');
    if (pref === "1") return readAccessibilityIcon;
    if (pref === "2") return eyeAccessibilityIcon;
    return accessibilityIcon;
  };

  useEffect(() => {

    const cookiePref = getCookie('@IBFEOnline:accessibility');
    const localPref = localStorage.getItem('user_accessibility');

    if (cookiePref && cookiePref !== localPref) {
      localStorage.setItem('user_accessibility', cookiePref);

      if (cookiePref !== '99') {
        localStorage.setItem('accessibility_onboarding_done', 'true');
      }

      setCurrentPref(cookiePref);
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    const corrigirBotaoReset = (e) => {

      const elementoClicado = e.target.closest('._menu-reset-btn');

      if (elementoClicado) {
        Object.keys(localStorage).forEach(key => {
          if (key.toLowerCase().includes('access') && key !== 'user_accessibility') {
            localStorage.removeItem(key);
          }
        });

        Object.keys(sessionStorage).forEach(key => {
          if (key.toLowerCase().includes('access')) {
            sessionStorage.removeItem(key);
          }
        });

        if (document.documentElement) {
          document.documentElement.removeAttribute('style');
        }

        if (document.body) {
          document.body.removeAttribute('style');
          document.body.classList.remove('_access_cursor');
          document.body.className = document.body.className.replace(/_access\S*/g, '').trim();
        }

        const guide = document.getElementById('access_read_guide_bar');
        if (guide) guide.remove();
      }
    };

    document.addEventListener('click', corrigirBotaoReset, true);
    return () => document.removeEventListener('click', corrigirBotaoReset, true);
  }, []);

  useEffect(() => {

    let pref = localStorage.getItem('user_accessibility');

    if (!pref && currentPref && currentPref !== '99') {
      pref = currentPref;
      localStorage.setItem('user_accessibility', String(currentPref));
    } else if (!pref) {
      pref = '99';
    }

    setCurrentPref(pref);

    if (pref === "1" || pref === "2") {
      initAccessibility(pref);
    }
  }, [(auth && auth.user), window.location.pathname, currentPref, initAccessibility]);

  // faz o navegador falar
  useEffect(() => {
    const pref = localStorage.getItem('user_accessibility');
    if (pref === '1') {
      setTimeout(() => {
        const welcome = new SpeechSynthesisUtterance("Modo de leitura de voz ativado");
        welcome.lang = 'pt-BR';
        welcome.rate = 1.2;
        window.speechSynthesis.speak(welcome);
      }, 1000);
    }
  }, []);

  // reaplica tamanho do texto
  useEffect(() => {
    const pref = localStorage.getItem('user_accessibility');
    if (pref !== "2") return;
    const reaplicarEstado = () => {
      const savedSettings = localStorage.getItem('accessibility_settings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);

        if (settings.textSize && !document.body.classList.contains('_access_text_resize')) {
          document.body.classList.add('_access_text_resize');
        }
      }
    };

    const timer = setTimeout(reaplicarEstado, 200);
    return () => clearTimeout(timer);
  }, [window.location.pathname]);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'accessibility-override-styles';
    style.innerHTML = `
      body._access_text_resize p,
      body._access_text_resize span,
      body._access_text_resize div,
      body._access_text_resize h1,
      body._access_text_resize h2,
      body._access_text_resize h3 {
        font-size: inherit !important;
      }

      ._access_text_resize {
        font-size: 1.2rem !important;
      }

      .ba-video-container {
        position: relative !important;
        width: 100%;
        height: 100%;
      }

      .ba-player-overlay {
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        width: 100% !important;
        height: 100% !important;
        z-index: 10 !important;
        background: transparent !important;
        cursor: pointer !important;
        border: 3px solid transparent !important;
        box-sizing: border-box !important;
      }

      .ba-player-overlay:focus {
        // border-color: #99ba3f !important;
        outline: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const el = document.getElementById('accessibility-override-styles');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const ajustarPosicaoWidget = () => {
      const divVw = document.querySelector('div[vw]');
      if (divVw) {
        const temClasseAtiva = document.querySelector('._access-icon') || divVw.classList.contains('_access-icon');

        if (temClasseAtiva) {
          divVw.style.setProperty('bottom', '120px', 'important');
        } else {
          divVw.style.setProperty('bottom', '50px', 'important');
        }
      }
    };

    ajustarPosicaoWidget();

    const observer = new MutationObserver(() => {
      ajustarPosicaoWidget();
    });

    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, [currentPref]);

  const handleWidgetChoice = (value) => {
    localStorage.setItem('user_accessibility', String(value));

    setCurrentPref(value);

    const dataExpiracao = new Date();
    dataExpiracao.setDate(dataExpiracao.getDate() + 365);

    createCookie('@IBFEOnline:accessibility', String(value), dataExpiracao);

    setCurrentPref(value);
    if (value === "99") {
      Object.keys(localStorage).forEach(key => {
        if (key.toLowerCase().includes('access') && key !== 'user_accessibility' && key !== 'accessibility_onboarding_done') {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach(key => {
        if (key.toLowerCase().includes('access')) {
          sessionStorage.removeItem(key);
        }
      });
    }

    setShowWidgetModal(false);
    window.location.reload();
  }

  return (
    <>
      <Routes />

      <button
        className="btn-floating-accessibility"
        onClick={() => setShowWidgetModal(true)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          backgroundColor: '#A2BA47',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          zIndex: 9999,
          fontSize: '24px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s',
        }}
      >
        <img
          src={getIcon()}
          alt="Acessibilidade"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </button>

      {showWidgetModal && (
        <ModalOverlay
          style={{ zIndex: 10000 }}
          onClick={() => setShowWidgetModal(false)}
        >
          <ModalContainer onClick={(e) => e.stopPropagation()}>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px'
              }}
            >
              <h2 style={{ margin: 0, textAlign: 'left', marginBottom: '24px' }}>
                Preferências de Acessibilidade
              </h2>

              <button
                onClick={() => setShowWidgetModal(false)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                  marginTop: '-56px',
                  background: 'transparent',
                  border: 'none',
                  color: '#000',
                  fontSize: '50px',
                  cursor: 'pointer',
                  fontWeight: '400',
                  lineHeight: 1,
                  transition: 'transform 0.2s ease',
                  transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                }}
              >
                ×
              </button>
            </div>

            <p style={{ textAlign: 'center', fontSize: '15px', color: '#333', marginBottom: '20px' }}>
              Você está navegando com o modo: <br />
              <strong>
                {currentPref === '1' && 'Leitura em Voz Alta'}
                {currentPref === '2' && 'Assistência Visual'}
                {(currentPref === '99' || currentPref === 'false') && 'Navegação Padrão'}
              </strong>.
            </p>

            <p style={{ textAlign: 'center' }}>Como você prefere navegar?</p>

            <div style={{ display: 'flex', gap: '15px', flexDirection: 'column' }}>

              {currentPref !== '1' && (
                <ButtonActive
                  onClick={() => handleWidgetChoice('1')}
                  style={{ backgroundColor: '#A2BA47' }}
                >
                  Ativar Leitura em Voz Alta
                </ButtonActive>
              )}

              {currentPref !== '2' && (
                <ButtonActive onClick={() => handleWidgetChoice('2')}>
                  Ativar Assistência Visual (Baixa Visão)
                </ButtonActive>
              )}

              {currentPref !== '99' && currentPref !== 'false' && (
                <ButtonNormal onClick={() => handleWidgetChoice('99')}>
                  Navegação Padrão
                </ButtonNormal>
              )}

            </div>

            <p style={{ marginTop: '20px', fontSize: '12px', color: '#888' }}>
              Você poderá alterar isso a qualquer momento nas configurações do perfil.
            </p>
          </ModalContainer>
        </ModalOverlay>
      )}

      <GlobalStyle />
      <HelpersStyle />
    </>
  );
}

function App() {
  return (
    // clientId teste: 1062351796810-406rt4j0h8f0u1bjctql40gochg519jr.apps.googleusercontent.com
    <Router>
      <AuthProvider>
        <GoogleOAuthProvider clientId="996643125501-d3ske9n7u363bg68df304sj2oshtcniu.apps.googleusercontent.com">
          <AppContent />
        </GoogleOAuthProvider>
      </AuthProvider>

      <GlobalStyle />
      <HelpersStyle />
    </Router>
  );
}

export default App;
